import { SortTh } from '../components/SortTh';
import { ExpandRow, Summary } from '../components/ExpandRow';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { INQUIRY_STATUS_LABELS, INQUIRY_STATUSES, type Inquiry, type InquiryStatus, type Paginated } from '@kontor/shared';
import { api, queryString } from '../api';
import { StatusBadge } from '../components/StatusBadge';
import { FilterBar, type FilterField } from '../components/filter/FilterBar';
import { useSession } from '../session';
import { useInquiryTypes } from '../inquiryTypes';

const dateFormat = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' });

export function InquiriesPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const status = (params.get('status') ?? '') as InquiryStatus | '';
  const type = params.get('type') ?? '';
  const page = Number(params.get('page') ?? 1);
  const sort = params.get('sort') ?? '-createdAt';
  const q = params.get('q') ?? '';
  const types = useInquiryTypes();

  const list = useQuery({
    queryKey: ['inquiries', { status, type, page, q, sort }],
    queryFn: () => api<Paginated<Inquiry>>('GET', `/inquiries${queryString({ status, type, page, q, sort, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });

  const onSort = (next: string) => update({ sort: next === '-createdAt' ? '' : next });

  // Ein Typ aus der Adresszeile, den der Mandant nicht definiert hat (z. B. ein alter Link), bleibt als eigener Eintrag wählbar
  const typeOptions = types.types.map((t) => ({ key: t.key, label: t.label }));
  if (type && !typeOptions.some((o) => o.key === type)) typeOptions.push({ key: type, label: types.label(type) });

  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };

  const fields: FilterField[] = [
    { key: 'status', label: 'Status', type: 'select', options: [{ value: '', label: 'Alle' }, ...INQUIRY_STATUSES.map((s) => ({ value: s, label: INQUIRY_STATUS_LABELS[s] }))] },
    ...(typeOptions.length > 0 ? [{ key: 'type', label: 'Art', type: 'select' as const, options: [{ value: '', label: 'Alle Arten' }, ...typeOptions.map((t) => ({ value: t.key, label: t.label }))] }] : []),
  ];

  return (
    <>
      <div className="page-head">
        <h1>Anfragen</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} insgesamt` : ''}</span>
        {can('inquiries.write') && <Link className="btn btn--primary btn--sm page-head__action" to="/anfragen/neu">Neue Anfrage</Link>}
      </div>

      <FilterBar
        search={{ value: q, placeholder: 'Name, E-Mail, Telefon oder Vorgangsnummer', onSubmit: (v) => update({ q: v }) }}
        fields={fields}
        values={{ status, type }}
        onChange={(key, value) => update({ [key]: value })}
        onReset={() => setParams(new URLSearchParams())}
      />

      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}

      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <SortTh label="Vorgang" sortKey="reference" sort={sort} onSort={onSort} />
              <SortTh label="Kontakt" sortKey="name" sort={sort} onSort={onSort} />
              <SortTh label="Art" sortKey="type" sort={sort} onSort={onSort} />
              <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
              <SortTh label="Eingang" sortKey="createdAt" sort={sort} onSort={onSort} first="desc" />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr>
                <td colSpan={5} className="muted">Wird geladen …</td>
              </tr>
            )}
            {list.data?.data.length === 0 && (
              <tr>
                <td colSpan={5} className="empty">
                  {q || status || type ? 'Keine Anfragen zu diesem Filter.' : 'Noch keine Anfragen. Sobald Ihre Webseite angebunden ist, erscheinen sie hier.'}
                </td>
              </tr>
            )}
            {list.data?.data.map((i) => (
              <ExpandRow
                key={i.id}
                summary={
                  <Summary
                    title={<Link to={`/anfragen/${i.id}`} className="strong-link">{i.reference}</Link>}
                    aside={<StatusBadge status={i.status} />}
                    line2={`${i.contact.name} · ${types.label(i.type)}`}
                    line3={dateFormat.format(new Date(i.createdAt))}
                  />
                }
              >
                <td className="cell-desktop-title">
                  <Link to={`/anfragen/${i.id}`} className="strong-link">{i.reference}</Link>
                </td>
                <td data-label="Kontakt">
                  <span>
                    {i.contact.name}
                    <div className="muted small">{i.contact.email}</div>
                  </span>
                </td>
                <td data-label="Art">{types.label(i.type)}</td>
                <td data-label="Status"><StatusBadge status={i.status} /></td>
                <td data-label="Eingang" className="nowrap">{dateFormat.format(new Date(i.createdAt))}</td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>

      {list.data && list.data.meta.lastPage > 1 && (
        <nav className="pager" aria-label="Seiten">
          <button className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </nav>
      )}
    </>
  );
}
